<template>
	<div class="evolution_table_wrap" ref="scroller">
		<table class="evolution_table">
			<thead>
				<tr>
					<th class="row_head" scope="col"></th>
					<th v-for="bucket in buckets" :key="bucket" scope="col">{{ bucket }}</th>
				</tr>
			</thead>
			<tbody>
				<tr v-for="row in rows" :key="row.key"
					:class="{ 'is-tinted': row.tinted, 'is-strong': row.strong }"
					:style="row.tinted && row.colorToken ? { '--row-tint': `var(${row.colorToken}-soft)` } : null">
					<th class="row_head" scope="row">{{ row.label }}</th>
					<td v-for="(value, i) in row.values" :key="i">{{ formatValue(value, row.unit) }}</td>
				</tr>
			</tbody>
		</table>
	</div>
</template>

<script>
import { parseToMoney } from "@/utils/currency";

// Tabla de la card "Evolución de Ventas & Márgenes <ítem>" del drawer de
// categorías (Rentabilidad): la misma información que el gráfico por ítem,
// en filas — Ventas Netas y cada escalón en pesos y en % — con una columna
// por bucket del rango. Presentacional: recibe las filas ya armadas por la
// card (que las deriva de `evolution.metrics`), sin Vuex ni backend.
//
// La primera columna y el header van fijos: con un rango largo la tabla
// scrollea adentro de la card y el nombre de la fila no se pierde. El scroll
// horizontal arranca en el extremo DERECHO (los buckets más recientes, como
// las tablas de los tabs) y vuelve ahí cuando cambian las columnas; cambiar
// sólo los valores (otro ítem del mismo rango) respeta la posición elegida.
const EMPTY = "—";

export default {
	name: "ProfitabilityEvolutionTable",

	props: {
		// Etiquetas de las columnas, en el orden del eje X del gráfico.
		buckets: { type: Array, default: () => [] },
		// [{ key, label, unit: 'money'|'percent', values: [], colorToken?,
		//    tinted?, strong? }]. `values` trae un valor por bucket; `null`
		//    donde el gráfico corta la línea (bucket sin ventas).
		rows: { type: Array, default: () => [] },
	},

	watch: {
		buckets() {
			this.scrollToRight();
		},
	},

	mounted() {
		this.scrollToRight();
	},

	methods: {
		scrollToRight() {
			this.$nextTick(() => {
				const el = this.$refs.scroller;
				if (el) el.scrollLeft = el.scrollWidth;
			});
		},
		formatValue(value, unit) {
			if (value == null) return EMPTY;
			if (unit === "percent") return `${Math.round(value)}%`;
			return parseToMoney(value);
		},
	},
};
</script>

<style scoped lang="scss">
.evolution_table_wrap {
	// Mide lo que miden las filas y se encoge al alto del canvas si no
	// entran: ahí engancha su scroll interno. Sin crecimiento: el borde de la
	// tabla termina en la última fila, no en el fondo de la card.
	flex: 0 1 auto;
	min-width: 0;
	min-height: 0;
	overflow: auto;
	border: 1px solid var(--report-border);
	border-radius: 12px;
}

.evolution_table {
	width: 100%;
	border-collapse: separate;
	border-spacing: 0;
	font-size: 13px;
	font-variant-numeric: tabular-nums;

	th,
	td {
		padding: 10px 14px;
		text-align: right;
		white-space: nowrap;
		border-bottom: 1px solid var(--report-divider-soft);
		background: var(--report-bg);
	}

	thead th {
		position: sticky;
		top: 0;
		z-index: 2;
		font-weight: 600;
		color: var(--report-text-muted-strong);
		background: var(--report-surface);
	}

	// Primera columna fija: el nombre de la fila acompaña el scroll horizontal.
	.row_head {
		position: sticky;
		left: 0;
		z-index: 1;
		text-align: left;
		font-weight: 600;
		color: var(--report-text);
		min-width: 190px;
	}

	thead .row_head {
		z-index: 3;
	}

	td {
		color: var(--report-text-muted-strong);
	}

	tbody tr:last-child {
		th,
		td {
			border-bottom: 0;
		}
	}

	// Ventas Netas: la fila de referencia de la cascada.
	.is-strong td {
		font-weight: 600;
		color: var(--report-text);
	}

	// Las filas de % llevan el tinte suave de su escalón (mockup de Steve,
	// 2026-10-07): el color viene por fila como `--row-tint`.
	.is-tinted {
		th,
		td {
			background: var(--row-tint, var(--report-surface));
		}
	}
}
</style>
